import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, KeyRound, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import { formatarDataHora } from "@/lib/format";

interface ConfiguracaoDto {
  chave: string;
  valorMascarado: string;
  temValor: boolean;
  descricao?: string | null;
  criadoEm: string;
  atualizadoEm: string;
}

const CHAVES_CONHECIDAS: Array<{ chave: string; titulo: string; descricao: string; placeholder: string; valida?: (v: string) => string | null }> = [
  {
    chave: "gemini.api_key",
    titulo: "Chave da API Gemini (Google AI Studio)",
    descricao: "Usada pela análise de extrato com IA. Cadastre uma chave começando com 'AIza' obtida em aistudio.google.com.",
    placeholder: "AIza...",
    valida: (v) => (v.startsWith("AIza") && v.length >= 30 ? null : "A chave do Gemini começa com 'AIza' e tem ~39 caracteres."),
  },
  {
    chave: "gemini.modelo",
    titulo: "Modelo do Gemini",
    descricao: "Padrão: gemini-2.5-flash. Modelos mais recentes podem oferecer melhor qualidade ou preço.",
    placeholder: "gemini-2.5-flash",
  },
];

const schema = z.object({
  valor: z.string().min(1, "Valor obrigatório"),
  descricao: z.string().optional(),
});
type FormSchema = z.infer<typeof schema>;

export function ConfiguracoesPage() {
  const qc = useQueryClient();
  const [editando, setEditando] = useState<string | null>(null);
  const [removendo, setRemovendo] = useState<string | null>(null);
  const [mostrarSenha, setMostrarSenha] = useState(false);

  const { data: cadastradas, isLoading } = useQuery({
    queryKey: ["configuracoes"],
    queryFn: () => apiFetch<ConfiguracaoDto[]>("/api/admin/configuracoes"),
  });

  const salvar = useMutation({
    mutationFn: ({ chave, body }: { chave: string; body: FormSchema }) =>
      apiFetch(`/api/admin/configuracoes/${encodeURIComponent(chave)}`, { method: "PUT", body }),
    onSuccess: () => {
      toast.success("Configuração salva");
      qc.invalidateQueries({ queryKey: ["configuracoes"] });
      setEditando(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remover = useMutation({
    mutationFn: (chave: string) =>
      apiFetch(`/api/admin/configuracoes/${encodeURIComponent(chave)}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Configuração removida");
      qc.invalidateQueries({ queryKey: ["configuracoes"] });
      setRemovendo(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const form = useForm<FormSchema>({
    resolver: zodResolver(schema),
    defaultValues: { valor: "", descricao: "" },
  });

  const chaveEditando = CHAVES_CONHECIDAS.find((c) => c.chave === editando) ?? null;
  const cadastradaMap = new Map((cadastradas ?? []).map((c) => [c.chave, c]));

  const abrirEdicao = (chave: string) => {
    const existente = cadastradaMap.get(chave);
    form.reset({ valor: "", descricao: existente?.descricao ?? "" });
    setMostrarSenha(false);
    setEditando(chave);
  };

  const onSubmit = (body: FormSchema) => {
    if (!editando) return;
    const config = CHAVES_CONHECIDAS.find((c) => c.chave === editando);
    const erro = config?.valida?.(body.valor);
    if (erro) {
      form.setError("valor", { message: erro });
      return;
    }
    salvar.mutate({ chave: editando, body });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Configurações</h1>
          <p className="text-sm text-muted-foreground">
            Chaves de API e parâmetros usados pelas integrações do sistema.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="size-5" />
            Integrações disponíveis
          </CardTitle>
          <CardDescription>
            Edite cada chave clicando em &quot;Editar&quot;. Valores são mascarados após salvos — para visualizar a chave inteira é necessário cadastrar novamente.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-24" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Chave</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Atualizado em</TableHead>
                  <TableHead className="w-32">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {CHAVES_CONHECIDAS.map((c) => {
                  const cadastrada = cadastradaMap.get(c.chave);
                  return (
                    <TableRow key={c.chave}>
                      <TableCell>
                        <div className="font-medium">{c.titulo}</div>
                        <div className="text-xs text-muted-foreground">{c.chave}</div>
                      </TableCell>
                      <TableCell>
                        {cadastrada?.temValor ? (
                          <code className="rounded bg-muted px-2 py-1 text-xs">{cadastrada.valorMascarado}</code>
                        ) : (
                          <span className="text-xs italic text-muted-foreground">não cadastrada</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {cadastrada ? formatarDataHora(cadastrada.atualizadoEm) : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => abrirEdicao(c.chave)}>
                            <Pencil className="size-3.5" />
                            {cadastrada?.temValor ? "Editar" : "Cadastrar"}
                          </Button>
                          {cadastrada?.temValor ? (
                            <Button size="sm" variant="ghost" onClick={() => setRemovendo(c.chave)}>
                              <Trash2 className="size-3.5" />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={editando !== null} onOpenChange={(o) => !o && setEditando(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{chaveEditando?.titulo ?? "Editar configuração"}</DialogTitle>
            <DialogDescription>{chaveEditando?.descricao}</DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="valor">Valor</Label>
              <div className="flex gap-2">
                <Input
                  id="valor"
                  type={mostrarSenha ? "text" : "password"}
                  placeholder={chaveEditando?.placeholder}
                  autoComplete="off"
                  {...form.register("valor")}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setMostrarSenha((v) => !v)}
                  aria-label={mostrarSenha ? "Ocultar" : "Mostrar"}
                >
                  {mostrarSenha ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </Button>
              </div>
              {form.formState.errors.valor ? (
                <p className="text-xs text-destructive">{form.formState.errors.valor.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <Input id="descricao" placeholder="Ex: chave de produção" {...form.register("descricao")} />
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancelar
                </Button>
              </DialogClose>
              <Button type="submit" disabled={salvar.isPending}>
                {salvar.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={removendo !== null} onOpenChange={(o) => !o && setRemovendo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover configuração?</AlertDialogTitle>
            <AlertDialogDescription>
              A funcionalidade que depende dessa chave deixará de funcionar até ser cadastrada novamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => removendo && remover.mutate(removendo)}>
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
